import { GridColDef, GridColumnVisibilityModel } from "@mui/x-data-grid-pro";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Tooltip } from "@mui/material";
import moment from "moment";
import { DateFilterOperator, NumberFilterOperator, TaskStatusFilterOperator, TextFilterOperator } from "components/shared/filters";
import { TaskStatusCell } from "components/tasks";
import { formatDate } from "utils/date-utils";
import useGridColumns, { ColumnVisibilityOrOrderChangeHandler } from "hooks/shared/useGridColumns";
import { IDashboardSettings } from "components/preferences";
import { SetPreferenceSignature } from "hooks/preferences";
import { useFeatureEnabled } from "hooks/shared";
import { useTaskPermissions } from "hooks/tasks";

type UseTaskColumnsProps = {
  privilegesLoading: boolean,
  settings?: IDashboardSettings | null,
  setSettings?: SetPreferenceSignature,
};

type UseTaskColumnsResult = [
  GridColDef[],
  GridColumnVisibilityModel,
  ColumnVisibilityOrOrderChangeHandler,
];

type UseTaskColumnsSignature = (props: UseTaskColumnsProps) => UseTaskColumnsResult;

const useTaskColumns: UseTaskColumnsSignature = (props) => {
  const { privilegesLoading } = props;
  const { settings, setSettings } = props;
  const [ wave4Enabled ] = useFeatureEnabled('wave-4');
  const permissions = useTaskPermissions();

  const columnBase: GridColDef[] = [
    {
      field: 'workItemId', headerName: 'WI ID', width: 150, sortable: true, resizable: true, hideable: false, headerClassName: 'header', hideSortIcons: false, disableReorder: true,
      filterOperators: NumberFilterOperator,
      renderHeader: (params) => {
        return (
          <Tooltip title="Work Item ID" arrow>
            <div style={{ height: '3em' }} aria-label="Work Item ID" tabIndex={0}>{"WI ID"}</div>
          </Tooltip>
        )
      }
    },
    {
      field: 'workItemName', headerName: 'Work Item', flex: 1, minWidth: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false,
      filterOperators: TextFilterOperator,
      renderCell: (params) => {
        const name = params.value;
        const workItemId = params.row.workItemId;
        return (
          <Tooltip title={name} arrow>
            <div style={{ whiteSpace: 'break-spaces' }}>
              <a 
                href={wave4Enabled
                  ? process.env.REACT_APP_PUBLIC_URL + `/workitems/${workItemId}/overview`
                  : process.env.REACT_APP_CLASSIC_URL + `/WorkitemEnduser/EditWorkitem?workitemID=${workItemId}`}
                aria-label={`View work item ${name}`}
                tabIndex={0}
                style={{ color: '#0066CC', cursor: 'pointer', textDecoration: 'underline' }} 
                rel="noopener noreferrer"
              >
                {name}
              </a>
            </div>
          </Tooltip>
        );
      }
    },
    {
      field: 'workflowName', headerName: 'Workflow', minWidth: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false,
      filterOperators: TextFilterOperator,
      renderCell: (params) => {
        const name = params.value;
        const workflowId = params.row.workflowId;
        return (
          <Tooltip title={name} arrow>
            <div style={{ whiteSpace: 'break-spaces' }}>
              {permissions?.canEditWorkflows ?
                <a 
                  href={process.env.REACT_APP_CLASSIC_URL + `/workflowinstance/editworkflow?workflowInstanceId=${workflowId}`} 
                  aria-label={`Edit workflow ${name}`}
                  tabIndex={0}
                  style={{ color: '#0066CC', cursor: 'pointer', textDecoration: 'underline' }} 
                  rel="noopener noreferrer"
                >
                  {name}
                </a>
                : <span aria-label={`Workflow ${name}`}>{name}</span>
              }
            </div>
          </Tooltip>
        );
      }
    },
    {
      field: 'name', headerName: 'Task', minWidth: 200, sortable: true, resizable: true, hideable: false, headerClassName: 'header', hideSortIcons: false, disableReorder: true,
      filterOperators: TextFilterOperator,
      renderCell: (params) => {
        const name = params.value;
        const taskId = params.row.id;
        return (
          <Tooltip title={name} arrow>
            <div style={{ whiteSpace: 'break-spaces' }}>
              <a 
                onClick={() => navigateToTask(taskId)} 
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigateToTask(taskId);
                  }
                }}
                role="link"
                tabIndex={0}
                aria-label={`View task ${name}`}
                style={{ color: '#0066CC', cursor: 'pointer', textDecoration: 'underline' }} 
                rel="noopener noreferrer"
              >
                {name}
              </a>
            </div>
          </Tooltip>
        );
      }
    },
    {
      field: 'userName', headerName: 'Resource',
      headerClassName: 'header',
      sortable: true, hideSortIcons: false,
      filterOperators: TextFilterOperator,
      resizable: true, width: 200,
    },
    {
      field: 'status', headerName: 'Task Status',
      headerClassName: 'header',
      sortable: true, hideSortIcons: false,
      resizable: true, width: 200,
      filterOperators: TaskStatusFilterOperator,
      renderCell: (params) => {
        return (<TaskStatusCell status={params.value} />)
      }
    },
    {
      field: 'startDate', headerName: 'Start Date', width: 150, sortable: true, resizable: true, hideable: false, headerClassName: 'header', hideSortIcons: false, type: 'date',
      valueGetter: (value) => {
        return !value ? null : moment.utc(value).toDate();
      },
      filterOperators: DateFilterOperator,
      renderCell: (params) => {
        const startDate = params.value;
        const formattedDate = formatDate(startDate);
        return (
          <div aria-label={`Start Date: ${formattedDate || 'Not set'}`} tabIndex={0}>
            {formattedDate}
          </div>
        );
      }
    },
    {
      field: 'endDate', headerName: 'End Date', width: 150, sortable: true, resizable: true, hideable: false, headerClassName: 'header', hideSortIcons: false, type: 'date',
      valueGetter: (value) => {
        return !value ? null : moment.utc(value).toDate();
      },
      filterOperators: DateFilterOperator,
      renderCell: (params) => {
        const endDate = params.value;
        const formattedDate = formatDate(endDate);
        return (
          <div aria-label={`End Date: ${formattedDate || 'Not set'}`} tabIndex={0}>
            {formattedDate}
          </div>
        );
      }
    },
  ];

  const navigate = useNavigate();
  const [columns, setColumns] = useState<GridColDef[]>(columnBase);

  const navigateToTask = (taskId: number) => {
    navigate(`/tasks/${taskId}`);
  };

  const [columnsModel, visibilityModel, handleColumnVisibilityOrOrderChange] = useGridColumns({ columns: columns, settings, setSettings })

  useEffect(() => {
    let newColumns: GridColDef[] = [];
    newColumns.push(...columnBase);

    setColumns(newColumns);
}, [ privilegesLoading]);

  return [
    columnsModel,
    visibilityModel,
    handleColumnVisibilityOrOrderChange
  ];
};

export default useTaskColumns;